import type {
	RelationOutputForBuyer,
	RelationOutputForSeller,
} from "@/api/custom_types";
import { useTranslation } from "@/hooks/useTranslation";
import { currentUserAtom } from "@/store/login";
import { ActionItem, ActionMenu, Button, Icon } from "@st-marketplace/ui";
import { useAtomValue } from "jotai";
import { useState } from "react";
import RelationsFormEdit from "./components/form/RelationsFormEdit";

export type RelationRow = RelationOutputForBuyer | RelationOutputForSeller;

export interface RelationsSingleActionProps {
	selectedRelation: RelationRow;
	banPending: boolean;
	onToggleBan: (id: string, action: "BAN" | "UNBAN") => void;
	onAdjust: (relation: RelationRow) => void;
	display?: "menu" | "buttons";
	showAdjust?: boolean;
	showEdit?: boolean;
}

const RelationsSingleAction: React.FC<RelationsSingleActionProps> = ({
	selectedRelation,
	banPending,
	onToggleBan,
	onAdjust,
	display = "menu",
	showAdjust = true,
	showEdit = true,
}) => {
	const { t } = useTranslation();
	const user = useAtomValue(currentUserAtom);

	const [menuActionOpen, setMenuActionOpen] = useState<boolean>(false);
	const [editDialog, setEditDialog] = useState<boolean>(false);

	const willUnban = selectedRelation.state === "BANNED";
	const canEdit =
		"seller_id" in selectedRelation && selectedRelation.seller_id === user?.id;

	return (
		<>
			{display === "buttons" ? (
				<div className="flex flex-wrap items-center justify-end gap-2">
					{showAdjust && (
						<Button
							type="button"
							color="secondary"
							size="sm"
							startIcon={<Icon name="wallet-line" />}
							onClick={() => onAdjust(selectedRelation)}
						>
							{t("payment.adjustment.buttonText")}
						</Button>
					)}
					{showEdit && canEdit && (
						<Button
							type="button"
							color="secondary"
							size="sm"
							startIcon={<Icon name="edit-line" />}
							onClick={() => setEditDialog(true)}
						>
							{t("action.edit")}
						</Button>
					)}
					<Button
						type="button"
						color={willUnban ? "primary" : "danger"}
						size="sm"
						loading={banPending}
						startIcon={<Icon name={willUnban ? "check-line" : "forbid-line"} />}
						onClick={() =>
							onToggleBan(selectedRelation.id, willUnban ? "UNBAN" : "BAN")
						}
					>
						{willUnban
							? t("relation.tableActionUnban")
							: t("relation.tableActionBan")}
					</Button>
				</div>
			) : (
				<>
					{/* biome-ignore lint/a11y/useKeyWithClickEvents: stopPropagation-only container; inner menu remains keyboard-accessible */}
					<div onClick={(event) => event.stopPropagation()}>
						<ActionMenu open={menuActionOpen} onOpenChange={setMenuActionOpen}>
							{showAdjust && (
								<ActionItem
									leadingIconName="wallet-line"
									onClick={() => {
										setMenuActionOpen(false);
										onAdjust(selectedRelation);
									}}
								>
									{t("payment.adjustment.buttonText")}
								</ActionItem>
							)}
							{showEdit && (
								<ActionItem
									leadingIconName="edit-line"
									disabled={!canEdit}
									onClick={() => {
										setMenuActionOpen(false);
										setEditDialog(true);
									}}
								>
									{canEdit ? t("action.edit") : t("action.disallowed")}
								</ActionItem>
							)}
							{willUnban ? (
								<ActionItem
									leadingIconName="check-line"
									loading={banPending}
									onClick={() => {
										setMenuActionOpen(false);
										onToggleBan(selectedRelation.id, "UNBAN");
									}}
								>
									{t("relation.tableActionUnban")}
								</ActionItem>
							) : (
								<ActionItem
									leadingIconName="forbid-line"
									loading={banPending}
									onClick={() => {
										setMenuActionOpen(false);
										onToggleBan(selectedRelation.id, "BAN");
									}}
								>
									{t("relation.tableActionBan")}
								</ActionItem>
							)}
						</ActionMenu>
					</div>
				</>
			)}

			{editDialog && canEdit && (
				<RelationsFormEdit
					key={selectedRelation.id}
					selectedRelation={selectedRelation as RelationOutputForSeller}
					open={true}
					onClose={() => setEditDialog(false)}
				/>
			)}
		</>
	);
};

export default RelationsSingleAction;
